import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "About Us", path: "/about" },
  { name: "Service Areas", path: "/service-areas" },
  { name: "Contact", path: "/contact" },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm" data-testid="header">
      {/* Top bar */}
      <div className="bg-[#0F172A] text-white py-2">
        <div className="container-custom flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline">Mon-Fri: 7AM-6PM | Sat: 8AM-4PM</span>
            <span className="sm:hidden">Open 7AM-6PM</span>
          </div>
          <a 
            href="tel:+18884451244" 
            className="flex items-center gap-2 font-semibold hover:text-orange-400 transition-colors"
            data-testid="header-phone"
          >
            <Phone className="w-4 h-4" />
            <span>+1-888-445-1244</span>
          </a>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container-custom py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <div className="w-10 h-10 bg-[#F97316] rounded-lg flex items-center justify-center">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                CART GENIX
              </span>
              <span className="block text-xs text-slate-500 uppercase tracking-wider">Roof Repair Experts</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${isActive(link.path) ? "active" : ""}`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:+18884451244">
              <Button className="btn-primary" data-testid="header-cta">
                <Phone className="w-4 h-4" />
                Call Now
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t border-slate-200 animate-fadeInUp" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? "bg-orange-50 text-orange-500"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              <a href="tel:+18884451244" className="mt-2">
                <Button className="btn-primary w-full" data-testid="mobile-cta">
                  <Phone className="w-4 h-4" />
                  Call Now: 888-445-1244
                </Button>
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
